// Ekran TV (/tv/{alias}): wyniki w kolumnach „gazetowo” — w dół, potem następna kolumna.
// Czysta logika (bez Vue): lista pozycji, dopasowanie do ekranu, kolumny stałe i strony ostatniej kolumny,
// „Właśnie na mecie”. Wymiary w px dla ekranu 1920×1080 (makieta TV1, kanwa „Wyniki – makiety”); przy innym
// ekranie mnożone przez krótszy bok / 1080, więc TV w 1080p i w 4K pokazują to samo, a ekran pionowy
// ma te same litery i więcej wierszy w kolumnie.

export const DESIGN = Object.freeze({
  height: 1080,
  header: 88,
  footer: 56,
  padX: 28, // margines z boku (telewizory przycinają brzegi obrazu)
  padY: 14,
  gap: 14,
  colHead: 36,
  border: 2,
  colPerRow: 12.5, // najwęższa kolumna = 12,5 wysokości wiersza (miejsce, numer, nazwisko, kategoria, czas)
  colMaxPerRow: 16, // najszersza — szersza dałaby tylko pustą przerwę między nazwiskiem a czasem
  maxRow: 84, // największy wiersz przy bardzo małej liście
  minRows: 6
})

export const PAGE_MS = 8000 // strona ostatniej kolumny
export const ROUTE_MIN_MS = 30000 // kilka tras: najkrócej tyle na jednej trasie
export const RECENT_MS = 10 * 60 * 1000 // „Właśnie na mecie”: meta z ostatnich 10 min
export const RECENT_LIMIT = 3
export const FRESH_MS = 2 * 60 * 1000 // podświetlenie świeżej mety (jak na /finish)
export const SMOOTH_SPEED = 25 // płynne przewijanie: px na sekundę (przy 1080 px)
export const SMOOTH_PAUSE_S = 3 // postój na początku i na końcu listy

const FIXED_HEIGHT = DESIGN.header + DESIGN.footer + 2 * DESIGN.padY + DESIGN.colHead + DESIGN.border

export function screenFrame(width, height) {
  const scale = Math.max(0.3, Math.min(width, height) / DESIGN.height)
  return {
    scale,
    bodyHeight: Math.max(60, height - FIXED_HEIGHT * scale),
    innerWidth: Math.max(200, width - 2 * DESIGN.padX * scale),
    gap: DESIGN.gap * scale
  }
}

export function columnsThatFit(innerWidth, gap, rowHeight) {
  const min = rowHeight * DESIGN.colPerRow
  return Math.max(1, Math.floor((innerWidth + gap) / (min + gap)))
}

/**
 * Dopasowanie do ekranu.
 * @param {{ width, height, count, rows }} p  rows = wierszy w kolumnie na ekranie 16:9 z parametru „litery”
 *   (20 / 25 / 32) — wyznacza wysokość wiersza; ekran pionowy albo panoramiczny mieści ich więcej.
 * @returns {{ rowHeight, perColumn, columns, fits, scale, capacity }}
 *   fits = wszystko mieści się bez przewijania; wtedy wiersze rosną (największe litery, przy których
 *   nazwiska nadal się mieszczą), a kolumny mają równą wysokość.
 */
export function fitLayout({ width, height, count, rows = 25 }) {
  const f = screenFrame(width, height)
  const presetRow = Math.max(8, Math.floor(((DESIGN.height - FIXED_HEIGHT) / rows) * f.scale))
  const presetCapacity = Math.max(1, Math.floor(f.bodyHeight / presetRow))
  const presetColumns = columnsThatFit(f.innerWidth, f.gap, presetRow)
  if (count > presetColumns * presetCapacity) {
    return { rowHeight: presetRow, perColumn: presetCapacity, columns: presetColumns, fits: false, scale: f.scale, capacity: presetCapacity }
  }
  const maxRow = Math.max(presetRow, Math.round(DESIGN.maxRow * f.scale))
  let rowHeight = presetRow
  for (let h = presetRow + 1; h <= maxRow; h++) {
    const capacity = Math.floor(f.bodyHeight / h)
    if (capacity < DESIGN.minRows && capacity < count) break
    if (capacity * columnsThatFit(f.innerWidth, f.gap, h) >= count) rowHeight = h
  }
  const capacity = Math.max(1, Math.floor(f.bodyHeight / rowHeight))
  const columns = Math.max(1, Math.min(columnsThatFit(f.innerWidth, f.gap, rowHeight), Math.ceil(Math.max(1, count) / capacity)))
  // kolumny równej wysokości (ostatnia najwyżej krótsza), a nie pełne + resztka
  const perColumn = Math.min(capacity, Math.max(1, Math.ceil(count / columns)))
  return { rowHeight, perColumn, columns, fits: true, scale: f.scale, capacity }
}

export function courseLabel(phase) {
  if (phase === 'before') return 'Lista startowa'
  if (phase === 'ended') return 'Bez mety'
  return 'W trasie'
}

/**
 * Pozycje listy: na mecie, nagłówek + w trasie (przed startem: lista startowa), nagłówek + nie ukończyli,
 * na końcu jedna linia z liczbą DNS. Wiersze z buildResultsRows (utils/results-list.js).
 */
export function buildTvItems(rows, phase) {
  const g = { finished: [], course: [], dnf: [], dns: [] }
  rows.forEach(row => { if (g[row.group]) g[row.group].push(row) })
  const items = g.finished.map(row => ({ key: `r${row.id}`, row }))
  if (g.course.length) {
    items.push({ key: 'h-course', section: `${courseLabel(phase)} · ${g.course.length}` })
    g.course.forEach(row => items.push({ key: `r${row.id}`, row }))
  }
  if (g.dnf.length) {
    items.push({ key: 'h-dnf', section: `Nie ukończyli · ${g.dnf.length}` })
    g.dnf.forEach(row => items.push({ key: `r${row.id}`, row }))
  }
  if (g.dns.length) items.push({ key: 'n-dns', note: `Nie wystartowali (DNS): ${g.dns.length}` })
  return {
    items,
    counts: { finished: g.finished.length, course: g.course.length, dnf: g.dnf.length, dns: g.dns.length }
  }
}

/**
 * Podział na kawałki po `size`. Nagłówek grupy nie zostaje sam na dole kawałka — dostaje
 * wtedy pusty wiersz przed sobą i przechodzi do następnego (gdy `keepHeaders`).
 */
export function chunkItems(items, size, keepHeaders = true) {
  const n = Math.max(1, size)
  const chunks = []
  let current = []
  for (const item of items) {
    if (keepHeaders && n > 2 && item.section && current.length === n - 1) {
      current.push({ key: `gap-${item.key}`, spacer: true })
    }
    if (current.length >= n) {
      chunks.push(current)
      current = []
    }
    current.push(item)
  }
  if (current.length) chunks.push(current)
  return chunks
}

/**
 * Kolumny stałe i strony ostatniej kolumny.
 * @param {Array} items     buildTvItems().items
 * @param {Object} layout   fitLayout()
 * @param {number} pinned   ile osób w „Właśnie na mecie” nad stronami (zajmują pinned + 1 wiersz)
 * @returns {{ fixed: Array[], pages: Array[], rest: Array, perPage: number }}
 */
export function splitColumns(items, layout, pinned = 0) {
  if (layout.fits) {
    // bez pustych wierszy, gdyby przez nie zabrakło kolumny
    let size = layout.perColumn
    let fixed = chunkItems(items, size)
    while (fixed.length > layout.columns && size < (layout.capacity || size)) {
      size += 1
      fixed = chunkItems(items, size)
    }
    if (fixed.length > layout.columns) fixed = chunkItems(items, layout.perColumn, false)
    return { fixed, pages: [], rest: [], perPage: 0 }
  }
  const all = chunkItems(items, layout.perColumn)
  const fixed = all.slice(0, Math.max(0, layout.columns - 1))
  const rest = all.slice(fixed.length).flat().filter(item => !item.spacer)
  const perPage = Math.max(1, layout.perColumn - (pinned ? pinned + 1 : 0))
  return { fixed, pages: chunkItems(rest, perPage), rest, perPage }
}

// „Miejsca 1–25”, „Miejsca 139–142 · w trasie”, „W trasie · nie ukończyli”
export function columnLabel(chunk, phase) {
  const places = chunk.filter(item => item.row && item.row.place != null).map(item => item.row.place)
  const groups = new Set(chunk.filter(item => item.row).map(item => item.row.group))
  const parts = []
  if (places.length) {
    const firstPlace = places[0]
    const lastPlace = places[places.length - 1]
    parts.push(firstPlace === lastPlace ? `Miejsce ${firstPlace}` : `Miejsca ${firstPlace}–${lastPlace}`)
  } else if (groups.has('finished')) {
    parts.push('Na mecie')
  }
  if (groups.has('course')) parts.push(courseLabel(phase).toLowerCase())
  // obok miejsc krótko (nagłówek kolumny ma jedną linię), sama grupa — pełną nazwą
  if (groups.has('dnf')) parts.push(parts.length ? 'DNF' : 'nie ukończyli')
  const label = parts.join(' · ')
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : ''
}

// „PK1 Przełęcz” → „PK1” (kod punktu), inne nazwy bez zmian — do wąskiej kolumny czasu przy DNF
export function shortPoint(text) {
  const value = String(text || '').trim()
  const code = value.split(/\s+/)[0]
  return /^[A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż]{1,4}\d{1,3}$/.test(code) ? code : value
}

function finishedRecently(row, nowMs, windowMs) {
  if (row.group !== 'finished' || row.finishMs == null) return false
  const age = nowMs - row.finishMs
  return age >= -60000 && age <= windowMs // minuta zapasu na różnicę zegarów
}

// Do 3 osób z metą z ostatnich 10 min, najnowsza pierwsza — wg godziny mety, nie miejsca (start interwałowy)
export function recentFinishers(rows, nowMs, limit = RECENT_LIMIT) {
  return rows
    .filter(row => finishedRecently(row, nowMs, RECENT_MS))
    .sort((a, b) => b.finishMs - a.finishMs)
    .slice(0, limit)
}

export function isFresh(row, nowMs) {
  return !!row && finishedRecently(row, nowMs, FRESH_MS)
}
